import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import * as cognito from '../library/cognito';
import userService from '../services/userService';

export const AUTH_LOADING = 'Loading';
export const AUTH_SIGNEDIN = 'SignedIn';
export const AUTH_SIGNEDOUT = 'SignedOut';

/*
export interface IAuth {
  authStatus?: AuthStatus;
  authUser?: User;
  authUserStatus?: 'idle' | 'error' | 'loading' | 'success';
  isLoading?: boolean;
  loginError?: string | null;
  session?: cognito.UserSession;
  signInWithEmail?: (props: AuthenticateProps) => void;
  signOut?: () => void;
  changePassword?: (props: AuthenticateProps) => void;
}

*/
const defaultState = {
  authStatus: AUTH_LOADING,
  authUser: null,
};

const LOCKED_OUT_MESSAGE =
  'Too many failed attempts. Your account is temporarily locked. Wait 15 minutes, then sign in again.';

// Cognito reports lockouts as NotAuthorizedException with "Password attempts
// exceeded" or "Too many invalid credentials attempts" in the message.
const isLockedOut = (err) =>
  err?.name === 'NotAuthorizedException' && /attempts/i.test(err.message);

export const AuthContext = React.createContext(defaultState);

const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [otpPending, setOtpPending] = useState(false);
  const [otpDestination, setOtpDestination] = useState();
  const [remember, setRemember] = useState(false);
  const [authStatus, setAuthStatus] = useState(AUTH_LOADING);
  const [userEmail, setUserEmail] = useState();
  const [session, setSession] = useState();
  const { data: authUser, status: authUserStatus } = useQuery(
    ['currentUser', userEmail],
    () => userService.getByEmail(userEmail.toLowerCase()),
    {
      enabled: !!userEmail && !!session,
      retry: 0,
    }
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    async function getSessionInfo() {
      try {
        const [userSession, attributes] = await getSessionAttributes();
        axios.defaults.headers.common['Authorization'] =
          userSession.tokens.IdToken;
        setSession(userSession);
        setUserEmail(attributes['email']);
        setAuthStatus(AUTH_SIGNEDIN);
      } catch (err) {
        setSession(undefined);
        setUserEmail(undefined);
        setAuthStatus(AUTH_SIGNEDOUT);
      }
    }
    getSessionInfo();
  }, [setAuthStatus, authStatus]);

  if (authStatus === AUTH_SIGNEDIN && authUserStatus === 'error') {
    signOut();
  }

  if (authStatus === AUTH_LOADING) {
    return null;
  }

  function resetLoginState() {
    setLoginError(null);
    setOtpPending(false);
    setOtpDestination(undefined);
    setRemember(false);
  }

  function signOut() {
    axios.defaults.headers.common['Authorization'] = null;
    cognito.logout();
    resetLoginState();
    setSession(undefined);
    setUserEmail(undefined);
    setAuthStatus(AUTH_SIGNEDOUT);
  }

  function completeSignIn(session) {
    axios.defaults.headers.common['Authorization'] = session.tokens.IdToken;
    setSession(session);
    resetLoginState();
    setAuthStatus(AUTH_SIGNEDIN);
  }

  function signInWithEmail(props) {
    // cognito.login() converts email to lowercase
    setIsLoading(true);
    resetLoginState();
    setRemember(!!props.remember);
    cognito
      .login(props)
      .then((result) => {
        if (result.otpRequired) {
          setOtpDestination(result.destination);
          setOtpPending(true);
        } else {
          completeSignIn(result.session);
          queryClient.invalidateQueries(['currentUser', props.username]);
        }
      })
      .catch((err) => {
        // cognito errors can include user not found so this should just go back to regular login rather than throw an error here
        axios.defaults.headers.common['Authorization'] = null;
        setSession(undefined);
        setUserEmail(undefined);
        setAuthStatus(AUTH_SIGNEDOUT);
        setLoginError(
          isLockedOut(err)
            ? LOCKED_OUT_MESSAGE
            : 'Username / password combination not found.'
        );
      })
      .then(() => {
        setIsLoading(false);
      });
  }

  function confirmOtp(code) {
    setIsLoading(true);
    setLoginError(null);
    cognito
      .confirmEmailOtp(code, remember)
      .then(completeSignIn)
      .catch((err) => {
        if (err?.name === 'CodeMismatchException') {
          setLoginError(
            'Incorrect code. Each code is only valid for 3 minutes.'
          );
          return;
        }
        // Lockouts and expired sessions can't be retried with this code, so
        // send the user back to sign in again for a new one.
        resetLoginState();
        setLoginError(
          isLockedOut(err)
            ? LOCKED_OUT_MESSAGE
            : 'Your code expired. Sign in again to get a new code.'
        );
      })
      .then(() => setIsLoading(false));
  }

  async function changePassword(props) {
    await cognito.changePassword(props);
  }

  async function getSessionAttributes() {
    try {
      const sessionAttributs = await cognito.getUserSessionAttributes();
      return sessionAttributs;
    } catch (err) {
      throw err;
    }
  }

  const state = {
    AUTH_LOADING,
    AUTH_SIGNEDIN,
    AUTH_SIGNEDOUT,
    authStatus,
    authUser,
    authUserStatus,
    cancelOtp: resetLoginState,
    changePassword,
    confirmOtp,
    isLoading,
    loginError,
    otpDestination,
    otpPending,
    session,
    signInWithEmail,
    signOut,
  };

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
